import { PagesId } from '@ts-enums/PagesId'
import { ContentType } from '@ts-types/ArticleSection'
import { Translations } from '@ts-types/Translations'
import { getUrlByPageId } from '@utils/getUrlByPageId'
import { Languages } from '@wicadu/langscope'

const globalTranslations: Translations = {
  es: {
    seo_title: 'Procesar devoluciones, cambios y reembolsos',
    seo_description: 'Aprende a procesar devoluciones, cambios y reembolsos para pedidos en wicadu, garantizando satisfacción del cliente y gestión de inventario',
    seo_keywords: 'cancelar pedidos, wicadu, gestión de inventario, cancelar pedido wicadu, guía cancelar pedido, ventas, compras, cancelar pedido paso a paso, tutorial cancelar pedido, cómo cancelar pedidos',
    page_id: PagesId.OrdersReturns,
    breadcrumbs: {
      home: 'Inicio',
      orders_home: 'Pedidos',
      returns: 'Devoluciones y reembolsos'
    },
    article: {
      title: 'Procesar devoluciones y reembolsos',
      intended: [
        `<strong>Propietarios</strong> o <strong>Administradores</strong> con acceso completo para gestionar pedidos y <em>configurar de la tienda</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST}>asistente de wicadu</a>.`,
        `<strong>Usuarios</strong> con permisos para administrar <em>ventas</em> o <em>compras</em> y <em>procesar devoluciones, cambios o reembolsos.</em>.`,
      ],
      about: {
        title: 'Acerca de devoluciones y reembolsos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Resuelve problemas posteriores a la <strong>venta</strong> o <strong>compra</strong> con herramientas que garantizan satisfacción del cliente y mantienen tu inventario actualizado con <strong>wicadu</strong>.',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `<strong>Reembolso</strong>: Devuelve el dinero total o parcial al cliente o proveedor.`
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `<strong>Devolución</strong>: Emitir un reembolso y restaurar el producto al inventario.`,
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  "<strong>Cambio</strong>: Sustituye un producto o servicio devuelto por otro, actualizando el stock correspondiente."
                ],
              },
            ]
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `Con <strong>wicadu</strong>, puedes procesar <em>devoluciones totales</em> o <em>parciales</em>, realizar múltiples devoluciones para un mismo pedido y para pedidos de la tienda en línea, automatizar reembolsos usando el método de pago original. Configura instrucciones de devolución o habilita devoluciones de autoservicio para que los clientes gestionen solicitudes directamente.`,
            ],
          },
        ],
      },
      before_begin: {
        title: 'Antes de empezar',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `Antes de procesar una devolución, cambio o reembolso, verifica lo siguiente:  `,
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `El pedido debe estar en estado <em>Entregado</em> en el módulo de <strong>Ventas</strong> o <strong>Compras</strong> en <strong>wicadu</strong>.`
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Asegúrate de tener permisos para procesar devoluciones, cambios o reembolsos. Consulta con tu administrador si no estás seguro.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Revisa los detalles del pedido para garantizar precisión, ya que estas acciones afectan el <strong>inventario</strong> y las <strong>finanzas</strong>.'
                ],
              },
            ],
          },
        ],
      },
      how_to: {
        title: 'Pasos para procesar una devolución y reembolso',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Acceder al módulo correspondiente: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Dirígete a la funcionalidad de <em>Ventas</em> o <em>Compras</em> en el <strong>asistente de wicadu</strong>, según si gestionas un pedido de cliente o de proveedor.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Seleccionar el pedido: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Haz clic en el pedido en estado Entregado para ver sus detalles.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Iniciar el proceso de devolución: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'En la sección <em>General</em>, selecciona el botón <strong>Generar una devolución</strong>.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Nota:</strong> Este botón solo estará disponible si el pedido está en estado <em>Entregado</em>.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Elegir los productos para devolución o reembolso: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Selecciona los productos a incluir en el proceso según el tipo de devolución, cambio o reembolso requerido.',
                ],
              },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Escoge todos los productos para una devolución total. `
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Selecciona productos específicos para una devolución parcial.`,
                    ],
                  },
                ]
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Nota:</strong> Puedes procesar múltiples sesiones de devolución para el mismo pedido si es necesario.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Revisar y confirmar la devolución: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Haz clic en <strong>Continuar</strong> para revisar el resumen de la devolución. Si todo está correcto, haz clic en <strong>Crear devolución</strong> para finalizar. El estado del pedido se actualizará a Preparado.',
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Siguientes pasos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Una vez que la devolución esté en estado <em>Preparado</em>, considera estas acciones: ',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Adjuntar documentos opcionales: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Carga archivos relacionados, como facturas, recibos, etiquetas de envío o comprobantes de entrega. Consulta:
                      <a href=${getUrlByPageId(PagesId.ManagingDocsForOrders, Languages.Spanish)} title='Guía para adjuntar documentos a una devolución'>Cómo adjuntar documentos a una devolución</a>`
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Cancelar una devolución: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Si la devolución se creó por error, puedes cancelarla mientras esté en estado <em>Preparado</em>. Aprende cómo:
                      <a href=${getUrlByPageId(PagesId.HowToCancelOrders, Languages.Spanish)} title="Guía para cancelar una devolución">Cómo cancelar una devolución</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Registrar pagos de reembolso:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Registra el método de pago usado para el reembolso (e.g., efectivo, tarjeta). Para pedidos de la tienda en línea de wicadu, los reembolsos se procesan automáticamente con el método de pago original. Consulta:
                      <a href=${getUrlByPageId(PagesId.ManagingPaymentsForOrders, Languages.Spanish)} title='Guia para registrar pagos de reembolso'>Cómo registrar pagos de reembolso</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Marcar la devolución como entregada:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Una vez recibidos los productos devueltos o completado el cambio, actualiza el estado de la devolución a <em>Entregado</em>. Más detalles:
                      <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.Spanish)} title='Guia para marcar una devolución como entregada'>Cómo marcar una devolución como entregada</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Verificar el inventario:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Confirma que el stock refleja los niveles actualizados tras la devolución o cambio.',
                      // ARTICLE IDEA
                      // `Confirma que el stock refleja los niveles actualizados tras la devolución o cambio. Consulta:
                      // <a href=${getUrlByPageId(PagesId.Home, Languages.Spanish)} title='Guia para verificar el inventario'>Cómo verificar el inventario</a>`,
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problemas Comunes',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>No veo el botón de devolución:</strong> Asegúrate de que el pedido esté en estado <em>Entregado</em> y tengas permisos suficientes.`
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>El inventario no se actualiza tras la devolución:</strong> Verifica que la devolución se finalizó y marcó como <em>Entregado</em>, luego revisa el módulo de <strong>inventario</strong>.'
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>El reembolso no se registra para pedidos en línea:</strong> Confirma que el método de pago original sigue siendo válido en la configuración de la tienda en línea de <strong>wicadu</strong>.'
            ],
          },
        ],
      },
    }
  },
  en: {
    seo_title: 'Process a return, exchange, or refund',
    seo_description: 'Learn how to process returns, exchanges, and refunds orders in wicadu, ensuring customer satisfaction and accurate inventory management.',
    seo_keywords: 'returns wicadu, exchanges wicadu, refunds wicadu, process returns in wicadu, how to process refunds wicadu, wicadu return process, how to handle returns, online store refunds',
    page_id: PagesId.OrdersReturns,
    breadcrumbs: {
      home: 'Home',
      orders_home: 'Orders',
      returns: 'Returns and refunds'
    },
    article: {
      title: 'Process a returns and refunds',
      intended: [
        `<strong>Store Owners</strong> or <strong>Admins</strong> with full access to manage orders and configure store settings within the <a href=${process.env.STORES_WEBAPP_HOST}>wicadu assistant</a>.`,
        `<strong>Users</strong> with permissions to handle <em>sales</em> or <em>purchases</em> and process <em>returns</em>, <em>exchanges</em>, or <em>refunds</em>.`
      ],
      about: {
        title: 'About returns, exchanges, and refunds',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Address post-sale or post-purchase issues with tools designed to ensure <strong>customer satisfaction</strong> and keep your <strong>inventory</strong> up to date with <strong>wicadu</strong>.'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `<strong>Refund</strong>: Return full or partial payment to the customer or supplier.`
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `<strong>Return</strong>: Issue a refund and restore the product to the inventory.`,
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  "<strong>Exchange</strong>: Replace a returned product or service with another one, updating stock accordingly."
                ],
              },
            ]
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>wicadu</strong> allows you to manage <em>full or partial returns</em>, handle multiple return sessions for a single order, and automate refunds for online store orders using the original payment method. Set up return instructions or enable self-service returns for customers to submit requests directly.'
            ],
          },
        ],
      },
      before_begin: {
        title: 'Before you begin',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Before processing a return, exchange, or refund, confirm the following:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  'The order must be in the <em>Delivered</em> state in the <strong>Sales</strong> or <strong>Purchases</strong> module in <strong>wicadu</strong>.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Ensure you have permissions to process returns, exchanges, or refunds. Check with your administrator if unsure.  '
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Verify the order details to ensure accuracy, as these actions may impact <strong>inventory</strong> and your <strong>financial records</strong>.'
                ],
              },
            ],
          },
        ],
      },
      how_to: {
        title: 'Steps to process a return, exchange, or refund',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Access the relevant module: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Navigate to the <em>Sales</em> or <em>Purchases</em> functionality in the <strong>wicadu assistant</strong>, depending on whether you are managing a customer or supplier order.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Select the order: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Click the order in the <em>Delivered</em> state to view its details.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Initiate the return process: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'In the <em>General</em> section, select the <strong>Create a return</strong> button.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Note:</strong> This option is only available for orders in the <em>Delivered</em> state.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Choose products for return, exchange, or refund: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Select the products to include in the process based on the type of return, exchange, or refund needed.'
                ],
              },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Choose all items for a <strong>full return</strong> or <strong>refund</strong>.`
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Select specific items for a <strong>partial return</strong> or <strong>exchange</strong>.`,
                    ],
                  },
                ]
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Note:</strong> You can process multiple return sessions for the same order if needed.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Review and confirm the return: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Click <strong>Continue</strong> to review the return summary. If everything is correct, click <strong>Create Return</strong> to finalize. The order status will update to <strong>Prepared</strong>.'
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Next steps',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Once the return is in <em>Prepared</em> status, consider these actions: '
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Attach optional documents: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Upload related files, such as invoices, receipts, shipping labels, or proof of delivery. See:
                      <a href=${getUrlByPageId(PagesId.ManagingDocsForOrders, Languages.English)} title='Attach documents to a return guide'>How to attach documents to a return</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Cancel a return: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `If the <strong>return</strong> was created by mistake, you can cancel it while it's in <em>Prepared</em> status. Learn how:
                      <a href=${getUrlByPageId(PagesId.HowToCancelOrders, Languages.English)} title="Cancel a return guide">How to cancel a return</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Record refund payments:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Record the payment method used for the refund (e.g., cash, card). For wicadu online store orders, refunds are automatically processed using the original payment method. See:
                      <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.English)} title='Record refund payments guide'>How to record refund payments</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Mark the return as delivered:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Once returned products are received or the exchange is completed, update the return status to Delivered.`,
                      // Learn more: <a href=${getUrlByPageId(PagesId.Home, Languages.English)} title='Mark a return as delivered guide'>How to mark a return as delivered</a>,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Verify inventory:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Confirm that the stock reflects the updated levels after the return or exchange.',
                      // ARTICLE IDEA
                      // `Confirm that the stock reflects the updated levels after the return or exchange. See:
                      // <a href=${getUrlByPageId(PagesId.Home, Languages.English)} title='Verify inventory guide'>How to verify inventory</a>`,
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Common issues',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>Can't see the Return/Refund button:</strong> Ensure the order is in the <em>Delivered</em> state and you have sufficient permissions.`
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Inventory not updated after return:</strong> Verify the return was finalized and marked as <em>Delivered</em>, then check the inventory module.'
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Refund not recorded for online orders:</strong> Confirm the original payment method is still valid in the <strong>wicadu</strong> online store settings.'
            ],
          },
        ],
      },
    }
  },
  fr: {
    seo_title: 'Retours et remboursements',
    seo_description:
      'Découvrez comment traiter les retours, échanges et remboursements dans wicadu tout en gardant le suivi des commandes cohérent.',
    seo_keywords:
      'retours commande, remboursements, échanges, service après-vente, wicadu commandes',
    page_id: PagesId.OrdersReturns,
    breadcrumbs: {
      home: 'Accueil',
      orders_home: 'Commandes',
      returns: 'Retours et remboursements',
    },
    article: {
      title: 'Retours et remboursements',
      intended: [
        `<strong>Propriétaires</strong>, <strong>administrateurs</strong> et équipes qui gèrent les <em>retours</em> et <em>remboursements</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST}>assistant wicadu</a>.`,
        '<strong>Vendeurs</strong> ou équipes de support chargés de traiter les demandes après-vente.',
      ],
      about: {
        title: 'À propos des retours et remboursements',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              "Les <strong>retours</strong>, <strong>échanges</strong> et <strong>remboursements</strong> vous permettent de corriger une commande après la vente lorsqu’un produit doit être repris, remplacé ou remboursé. Ce flux est important pour garder une gestion cohérente des <strong>commandes</strong>, du <strong>stock</strong> et des <strong>paiements</strong> dans <strong>wicadu</strong>.",
              `Un processus clair améliore l’<strong>expérience client</strong>, aide votre équipe à mieux suivre les opérations après-vente et garantit que les mouvements d’inventaire, les documents et les remboursements restent alignés avec la situation réelle.`,
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `<strong>Remboursement</strong> : retour total ou partiel du paiement au client ou au fournisseur.`
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<strong>Retour</strong> : retour d’un produit avec réintégration dans l’inventaire et remboursement si nécessaire.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  "<strong>Échange</strong> : remplacement d’un produit ou service retourné par un autre, avec mise à jour du stock en conséquence."
                ],
              },
            ]
          },

        ],
      },
      before_begin: {
        title: 'Avant de commencer',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Avant de traiter un <strong>retour</strong>, un <strong>échange</strong> ou un <strong>remboursement</strong>, confirmez les points suivants :',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  'La commande doit être dans l’état <em>Livrée</em> dans le module <strong>Ventes</strong> ou <strong>Achats</strong> de <strong>wicadu</strong>.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Assurez-vous de disposer des autorisations nécessaires pour traiter des retours, des échanges ou des remboursements. En cas de doute, vérifiez avec votre administrateur.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Vérifiez soigneusement les détails de la commande afin de garantir l’exactitude du traitement, car ces actions peuvent avoir un impact sur l’<strong>inventaire</strong> et vos <strong>enregistrements financiers</strong>.',
                ],
              },
            ],
          },
        ],
      },
      how_to: {
        title: 'Comment traiter un retour ou un remboursement',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Accéder au module concerné : '] },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Rendez-vous dans la section <em>Ventes</em> ou <em>Achats</em> de l’<strong>assistant wicadu</strong>, selon que vous gérez une commande client ou fournisseur.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner la commande : '] },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Cliquez sur la commande en état <em>Livrée</em> pour afficher ses détails.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Lancer le processus de retour : '] },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Dans la section <em>Général</em>, sélectionnez le bouton <strong>Créer un retour</strong>.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Remarque :</strong> cette option n’est disponible que pour les commandes en état <em>Livrée</em>.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Choisir les produits à retourner, échanger ou rembourser : '] },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Sélectionnez les produits à inclure dans le processus selon le type de retour, d’échange ou de remboursement nécessaire.',
                ],
              },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Choisissez tous les articles pour un <strong>retour complet</strong> ou un <strong>remboursement complet</strong>.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Sélectionnez seulement certains articles pour un <strong>retour partiel</strong> ou un <strong>échange</strong>.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Remarque :</strong> vous pouvez traiter plusieurs sessions de retour pour une même commande si nécessaire.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Vérifier et confirmer le retour : '] },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Cliquez sur <strong>Continuer</strong> pour vérifier le résumé du retour. Si tout est correct, cliquez sur <strong>Créer le retour</strong> pour finaliser. Le statut de la commande passera à <strong>Préparé</strong>.',
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Étapes suivantes',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Une fois que le retour est en état <em>Préparé</em>, vous pouvez poursuivre avec les actions suivantes :',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Joindre des documents optionnels : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Téléversez des fichiers liés au retour, comme des factures, des reçus, des étiquettes d’expédition ou des preuves de livraison. Consultez :
                      <a href=${getUrlByPageId(PagesId.ManagingDocsForOrders, Languages.French)} title='Comment joindre des documents à un retour dans wicadu'>Comment joindre des documents à un retour</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Annuler un retour : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Si le <strong>retour</strong> a été créé par erreur, vous pouvez l’annuler tant qu’il est dans l’état <em>Préparé</em>. Découvrez comment :
                      <a href=${getUrlByPageId(PagesId.HowToCancelOrders, Languages.French)} title="Comment annuler un retour dans wicadu">Comment annuler un retour</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Enregistrer les paiements de remboursement : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Enregistrez le mode de paiement utilisé pour le remboursement, par exemple en espèces ou par carte. Pour les commandes de la boutique en ligne <strong>wicadu</strong>, les remboursements sont traités automatiquement avec le mode de paiement d’origine. Consultez :
                      <a href=${getUrlByPageId(PagesId.ManagingPaymentsForOrders, Languages.French)} title='Comment enregistrer des paiements de remboursement dans wicadu'>Comment enregistrer des paiements de remboursement</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Marquer le retour comme livré : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Une fois les produits retournés reçus ou l’échange terminé, mettez à jour le statut du retour en <em>Livré</em>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Vérifier l’inventaire : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Après le traitement du retour, assurez-vous que le <strong>stock</strong> des produits a bien été mis à jour.',
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problèmes fréquents',
        content_type: ContentType.UnorderedList,
        content: [
          { content_type: ContentType.Paragraph, content: ['<strong>Le montant remboursé est incorrect</strong> : revérifiez les lignes concernées et le paiement d’origine.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>Le stock n’est pas cohérent</strong> : contrôlez si le retour devait réintégrer ou non les produits.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>Le mauvais flux a été utilisé</strong> : distinguez annulation, retour et remboursement avant de confirmer.'] },
        ],
      },
    },
  },
  pt: {},
}


export default globalTranslations
